import React, { useEffect, useMemo, useRef, useState } from "react";

import OverlapLegend from "./overlapLegend";
import OverlapMenu from "./OverlapMenu";
import OverlapsPlot from "./SourceChart";
import Spinner from "react-bootstrap/Spinner";
import { noSeq, srceSeqIds } from "../../utils/utils";
import { parsePtObjDates } from "../../utils/parsers";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useSurveyMode } from "../../contexts/SurveyModeProvider";
import { fetchTreeLeaves } from "../../utils/phyloFetch";
import { buildConstraintSeqIds } from "../../utils/utils";
import { useApi } from "../../contexts/ApiProvider";
import { patientActions } from "../../actions";

function Overlaps(micro_id) {
  const [childKey, setChildKey] = useState(0);
  const [plotProps, setPlotProps] = useState(null);
  const survey = useSelector((state) => state.survey);
  const activeCase = useSelector((state) => state.survey.activeCase);
  const api = useApi();
  const dispatch = useDispatch();

  const ptObj = useSelector((state) => state.patient.patient);
  const ptId = useSelector((state) => state.patient.micro_id);
  const tree_names = useSelector((state) => state.patient.tree.name_map);
  const overlap_constraint = useSelector(
    (state) => state.patient.tree.overlap_constraint
  );
  const snp_dist = useSelector((state) => state.patient.snp_distance);
  const navigate = useNavigate();

  function extractSeqIds(name_map) {
    if (!name_map) {
      return [];
    } else {
      return Object.entries(name_map)
        .filter(([, value]) => value && Array.isArray(value))
        .map(([seqId]) => String(seqId));
    }
  }

  const survey_mode =
    survey.surveyHistory[activeCase?.micro_id]?.mode || 0;

  const { debug_mode } = useSurveyMode();
  const debug_survey_mode = useSelector((state) => state.survey.debug_survey_mode);
  const remountChild = () => {
    setChildKey((prevKey) => prevKey + 1);
  };

  useEffect(() => {
    remountChild();
    // Pre-calculate all necessary props here
    if (ptObj && survey_mode !== undefined) {
      const data = parsePtObjDates(ptObj, ptObj.constrain, ptObj.overlap_level);
      const seqIds = extractSeqIds(tree_names) || [];
      const noSeqData = noSeq(ptObj, tree_names) || [];
      const propsReady = {
        data,
        contam_period: ptObj.contam_period,
        level: ptObj.overlap_level,
        navigate,
        tree_seq_ids: seqIds,
        no_seq: noSeqData,
        survey_mode,
        debug_survey_mode,
      };
      setPlotProps(propsReady);
    }
  }, [ptObj, survey_mode, tree_names]);

  const constraintSeqIds = useMemo(() => {
    if (!ptObj) return [];
    return buildConstraintSeqIds(srceSeqIds(ptObj), ptObj.pt.seq_id);
  }, [ptObj]);
  const constraintSeqIdsKey = constraintSeqIds.join(",");
  const appliedConstraintKeyRef = useRef(null);

  useEffect(() => {
    if (!overlap_constraint) {
      appliedConstraintKeyRef.current = null;
      return;
    }
    if (!ptObj || constraintSeqIds.length === 0) return;
    const constraintKey = `${constraintSeqIdsKey}|${snp_dist}`;
    if (appliedConstraintKeyRef.current === constraintKey) return;

    let cancelled = false;
    const applyConstraints = async () => {
      dispatch(patientActions.constraintLoadingStart());
      dispatch(patientActions.constrainTree(constraintSeqIds));
      try {
        const constraints = await fetchTreeLeaves(
          api,
          micro_id.micro_id,
          snp_dist
        );
        if (cancelled || !constraints) return;
        appliedConstraintKeyRef.current = constraintKey;
        dispatch(patientActions.constrainOverlap(constraints));
      } catch (err) {
        console.error("Error applying overlap constraints in Overlaps view", err);
        if (!cancelled) {
          dispatch(patientActions.constrainTree([]));
          dispatch(patientActions.constrainByOverlap(false));
          dispatch(patientActions.constrainOverlap([]));
          appliedConstraintKeyRef.current = null;
        }
      } finally {
        // Always balance the loading counter even if the request was cancelled
        dispatch(patientActions.constraintLoadingEnd());
      }
    };

    applyConstraints();
    return () => {
      cancelled = true;
    };
  }, [
    overlap_constraint,
    constraintSeqIds,
    constraintSeqIdsKey,
    snp_dist,
    ptObj,
    api,
    micro_id.micro_id,
    dispatch,
  ]);

  if (!plotProps || ptId !== micro_id.micro_id) {
    return <Spinner animation="border" />;
  }

  return (
    <div className="overlap-container" style={{ height: "100%" }}>
      <div className="button-container" style={{ width: "100%" }}>
        <OverlapMenu 
          adm={ptObj.pt.adm_start === null}
          seqIds={srceSeqIds(ptObj)}
          indexSeqId={ptObj.pt.seq_id}
          survey_mode={survey_mode}
          debug_mode={debug_mode}
          debug_survey_mode={debug_survey_mode}
        />
      </div>
      <>
        <div style={{ paddingTop: "0px" }}></div>
        {ptObj.pt.adm_start === null ? (
          <p
            style={{
              paddingTop: "100px",
              paddingBottom: "100px",
              textAlign: "center",
            }}
          >
            {ptObj.pt.name} was not admitted within the selected incubation period of {ptObj.incubation} day(s).
          </p>
        ) : (
          <>
            <OverlapsPlot
              key={`${childKey}-${plotProps.level}`}
              {...plotProps}
            />
            <div className="overlap-container">
            {plotProps.tree_seq_ids &&
            (plotProps.survey_mode >= 3 ||
              (debug_mode && debug_survey_mode >= 3)) ? (
                <OverlapLegend pt_name={ptObj.pt.name} />
              ) : null}
            </div>
          </>
        )}
      </>
    </div>
  );
}

export default Overlaps;
